export interface LinkedInScopeIntent {
  /** Existing app Product access for Company Page publishing/analytics scopes. */
  organizationCapabilitiesEnabled: boolean;
  memberAnalyticsApproved: boolean;
  /**
   * Advertising API product approval, granted per-app in the LinkedIn Developer
   * Portal. Approval alone is not enough to read a real ad account on the
   * Development tier: each nine-digit Ad Account ID must also be mapped to the
   * app under Products > View Ad Accounts > Add Ad Account. See
   * `docs/analytics/LINKEDIN-ADS-ACCEPTANCE-CONTRACT.md`.
   */
  advertisingApiApproved: boolean;
  /**
   * Deliberately SEPARATE from `advertisingApiApproved`, and deliberately not
   * implied by it. LinkedIn grants `rw_ads` in the same product approval, but
   * `LINKEDIN-ADS-ACCEPTANCE-CONTRACT.md` scopes the first release read-only and
   * requires a distinct product/security decision before Mora asks a member for
   * campaign-write capability. Requesting it early would put "Manage your
   * advertising accounts" on the consent screen for a feature that does not
   * exist. The cost of this split is one extra reconnect when writes ship.
   */
  advertisingWriteEnabled: boolean;
}

type LinkedInBuildEnvironment = {
  readonly [key: string]: unknown;
  VITE_LINKEDIN_ORGANIZATION_CAPABILITIES_ENABLED?: string;
  VITE_LINKEDIN_CM_APPROVED?: string;
};

type LinkedInAdsBuildEnvironment = {
  readonly [key: string]: unknown;
  VITE_LINKEDIN_ADS_CONNECTION_ENABLED?: string;
  VITE_LINKEDIN_ADS_WRITE_ENABLED?: string;
  VITE_LINKEDIN_ADS_CONVERSIONS_ENABLED?: string;
  VITE_LINKEDIN_LEADSYNC_ENABLED?: string;
  VITE_LINKEDIN_ADVERTISING_API_APPROVED?: string;
};

/**
 * Public build admission for the separate LinkedIn Ads consent path. This flag
 * only exposes the connect action; a real stored scope/account/provider probe
 * remains mandatory before reporting is admitted.
 */
export function linkedInAdsConnectionEnabled(environment: LinkedInAdsBuildEnvironment): boolean {
  return environment.VITE_LINKEDIN_ADS_CONNECTION_ENABLED !== 'false';
}

/**
 * Whether LinkedIn has approved the Advertising API product for this app —
 * the fact `buildCanonicalLinkedInScopes` actually needs to decide whether
 * `r_ads`/`r_ads_reporting` (and, gated further, `rw_ads`) belong in the
 * canonical request.
 *
 * 🔴 Lane G4, 2026-09-24. Before this function existed,
 * `buildCanonicalLinkedInScopes` reused `linkedInAdsConnectionEnabled` —
 * a UI-admission flag that only controls whether the "Connect LinkedIn Ads"
 * button is shown — to ALSO stand in for product approval. Those are two
 * different facts that happened to move together so far: nothing stopped a
 * future change to the UI flag (e.g. hiding the connect button for a
 * maintenance window) from silently changing the requested scope set and
 * re-triggering the exact "reconnecting one LinkedIn connection 401s the
 * other" incident `buildCanonicalLinkedInScopes`'s own docblock describes
 * (live 2026-09-24, fixed in `962611ab1`, reconnect still pending as of this
 * lane).
 *
 * `VITE_LINKEDIN_ADVERTISING_API_APPROVED` is the dedicated flag. It is
 * DELIBERATELY optional and, when unset, falls back to
 * `linkedInAdsConnectionEnabled` — the exact computation
 * `buildCanonicalLinkedInScopes` used before this function existed — so
 * introducing this flag changes NOTHING about the scope string this build
 * requests until an operator explicitly sets it. Setting it explicitly is
 * how the two facts get to diverge later without another silent scope
 * change: e.g. hiding the connect button (`VITE_LINKEDIN_ADS_CONNECTION_ENABLED=false`)
 * while advertising approval — and the request scope — stay exactly as they
 * are today (`VITE_LINKEDIN_ADVERTISING_API_APPROVED=true`).
 */
export function linkedInAdvertisingApiApproved(environment: LinkedInAdsBuildEnvironment): boolean {
  const explicit = environment.VITE_LINKEDIN_ADVERTISING_API_APPROVED;
  if (explicit !== undefined) return explicit !== 'false';
  return linkedInAdsConnectionEnabled(environment);
}

/**
 * Whether the separate `linkedin-ads` consent asks for `rw_ads` (campaign
 * mutation) alongside the read scopes.
 *
 * 🔴 Must agree with `linkedInAdsWriteEnabled()` in
 * `supabase/functions/linkedin-ads-oauth-callback/discovery.ts`, which reads the
 * server-side twin of this variable with the SAME `=== 'true'` predicate. The
 * previous LinkedIn Ads flag pair used opposite defaults and the result was a
 * completed OAuth round trip that silently produced no connection.
 *
 * `rw_ads` needs no separate LinkedIn application: it is granted automatically
 * with Advertising API approval, and verified accepted by LinkedIn's authorize
 * endpoint on 2026-09-09. Holding it is still not sufficient to mutate an
 * account — LinkedIn restricts it to members with ACCOUNT_BILLING_ADMIN /
 * ACCOUNT_MANAGER / CAMPAIGN_MANAGER / CREATIVE_MANAGER on that account.
 */
export function linkedInAdsWriteEnabled(environment: LinkedInAdsBuildEnvironment): boolean {
  return environment.VITE_LINKEDIN_ADS_WRITE_ENABLED === 'true';
}

/**
 * The Conversions API is a separate LinkedIn product. Its scope stays on the
 * Ads connection only: it authorizes conversion-rule/event management, never
 * Company Page publishing or the organic LinkedIn credential.
 *
 * It stays off until LinkedIn has granted the product on this application.
 * Deployment must set this explicitly to `true` only after that approval;
 * requesting an unavailable scope makes LinkedIn reject the whole OAuth
 * request before Mora's callback can explain the error.
 */
export function linkedInAdsConversionsEnabled(environment: LinkedInAdsBuildEnvironment): boolean {
  return environment.VITE_LINKEDIN_ADS_CONVERSIONS_ENABLED === 'true';
}

/**
 * The Lead Sync API is a separate LinkedIn program from the Advertising API.
 * Its scope reads lead form responses and manages leadNotifications, and like
 * the conversions scopes it rides the canonical list on both connections.
 *
 * It stays off until LinkedIn has granted the program on this application.
 * Flip order is client first, then server: the server twin
 * (`LINKEDIN_LEADSYNC_ENABLED` in `linkedin-ads-oauth-callback/discovery.ts`)
 * REQUIRES the scope when `true`, so flipping the server first would reject
 * every Ads connect. Same order as conversions; Justin flips after approval.
 * See `docs/integrations/LINKEDIN-SCOPE-WAVES-PLAN.md`.
 */
export function linkedInLeadSyncEnabled(environment: LinkedInAdsBuildEnvironment): boolean {
  return environment.VITE_LINKEDIN_LEADSYNC_ENABLED === 'true';
}

/**
 * Accepts the old build variable only while production configuration is
 * migrated. The legacy name is not a signal to enable Community features.
 */
export function linkedInOrganizationCapabilitiesEnabled(
  environment: LinkedInBuildEnvironment,
): boolean {
  const explicit = environment.VITE_LINKEDIN_ORGANIZATION_CAPABILITIES_ENABLED;
  if (explicit !== undefined) return explicit === 'true';
  return environment.VITE_LINKEDIN_CM_APPROVED === 'true';
}

const BASE_SCOPES = ['openid', 'profile', 'email', 'w_member_social'] as const;
const ORGANIZATION_SCOPES = [
  'w_organization_social',
  'rw_organization_admin',
  'r_organization_social',
] as const;
const MEMBER_ANALYTICS_SCOPES = ['r_member_postAnalytics', 'r_member_profileAnalytics'] as const;
/**
 * Granted by the Advertising API product. `r_ads` discovers ad accounts and
 * their campaign structure; `r_ads_reporting` retrieves delivery metrics. Both
 * are read-only, which is the whole of the first release.
 */
const ADVERTISING_READ_SCOPES = ['r_ads', 'r_ads_reporting'] as const;
/** Campaign mutation. Gated separately — see `advertisingWriteEnabled`. */
const ADVERTISING_WRITE_SCOPES = ['rw_ads'] as const;
/**
 * Conversions API product consent. LinkedIn's current product bundle requires
 * the write grant plus the member lite-profile grant; requesting only
 * `rw_conversions` produces a provider-side authorization failure before Mora's
 * callback can report a useful error.
 */
export const ADVERTISING_CONVERSION_SCOPES = ['rw_conversions', 'r_liteprofile'] as const;
/**
 * Lead Sync API consent. Reads lead form responses and manages
 * leadNotifications. Appended after the conversions scopes so every existing
 * scope keeps its position in the request.
 */
export const LEAD_SYNC_SCOPES = ['r_marketing_leadgen_automation'] as const;

/**
 * Builds the exact LinkedIn OAuth consent request from explicit product intent.
 * Product approval is a LinkedIn portal concern; this function never infers it
 * from an existing token or recorded scope value.
 */
export function buildLinkedInScopes({
  organizationCapabilitiesEnabled,
  memberAnalyticsApproved,
  advertisingApiApproved,
  advertisingWriteEnabled,
}: LinkedInScopeIntent): string[] {
  return [
    ...BASE_SCOPES,
    ...(organizationCapabilitiesEnabled ? ORGANIZATION_SCOPES : []),
    ...(memberAnalyticsApproved ? MEMBER_ANALYTICS_SCOPES : []),
    ...(advertisingApiApproved ? ADVERTISING_READ_SCOPES : []),
    ...(advertisingApiApproved && advertisingWriteEnabled ? ADVERTISING_WRITE_SCOPES : []),
  ];
}

type CanonicalLinkedInBuildEnvironment = LinkedInBuildEnvironment &
  LinkedInAdsBuildEnvironment & {
    VITE_LINKEDIN_MEMBER_ANALYTICS_APPROVED?: string;
  };

/**
 * 🔴 SUPERSEDES "ads scopes must never ride on organic" (OAUTH-SCOPES-LEDGER.md
 * §4, LINKEDIN-APPLICATION-READINESS.md — corrected 2026-09-24). That rule
 * protected against a real risk — LinkedIn fails the WHOLE authorize request
 * with `unauthorized_scope_error` if any ONE requested scope is unapproved —
 * and this function still protects against exactly that: every scope here is
 * still driven by the same product-approval/capability flags, never a
 * literal, so an unapproved scope still cannot reach a request.
 *
 * What the old rule missed is a SEPARATE LinkedIn behavior, verified
 * 2026-09-24 against LinkedIn's own docs (learn.microsoft.com/.../
 * authorization-code-flow, "Access Token Scopes and Lifetime"): "If you
 * request a different scope than the previously granted scope, all the
 * previous access tokens are invalidated." `linkedin` and `linkedin-ads`
 * share one clientId (VITE_LINKEDIN_CLIENT_ID) but requested DIFFERENT scope
 * sets, so reconnecting EITHER one silently invalidated the OTHER's token —
 * live incident: reconnecting ITC's organic Company Page connection at
 * 17:58 UTC 2026-09-24 401'd its LinkedIn Ads connection by 18:06, with no
 * alarm anywhere (see the LinkedIn OAuth tenancy project note in memory,
 * same date).
 *
 * The fix: BOTH flows request this SAME canonical list, always — every
 * authorize request this build issues asks for the identical scope set, so
 * "a different scope than previously granted" can never happen between them.
 * A guard test (`linkedin-scopes.test.ts`) pins this for every flag
 * combination.
 */
export function buildCanonicalLinkedInScopes(
  environment: CanonicalLinkedInBuildEnvironment,
): string[] {
  // Whether this build asks for the Advertising API's scopes at all —
  // `linkedInAdvertisingApiApproved`'s dedicated flag, falling back to the
  // public "is the separate Ads connection reachable" flag when unset (see
  // that function's docblock). Both `linkedin` and `linkedin-ads` call this
  // SAME function, so they still compute the identical answer from the
  // identical inputs — two flags that could drift between the connections is
  // the defect this whole module exists to remove; it is not reintroduced by
  // giving approval its own name.
  const advertisingApiApproved = linkedInAdvertisingApiApproved(environment);
  const base = buildLinkedInScopes({
    organizationCapabilitiesEnabled: linkedInOrganizationCapabilitiesEnabled(environment),
    memberAnalyticsApproved: environment.VITE_LINKEDIN_MEMBER_ANALYTICS_APPROVED === 'true',
    advertisingApiApproved,
    advertisingWriteEnabled: advertisingApiApproved && linkedInAdsWriteEnabled(environment),
  });
  const withConversions =
    advertisingApiApproved && linkedInAdsConversionsEnabled(environment)
      ? [...base, ...ADVERTISING_CONVERSION_SCOPES]
      : base;
  return advertisingApiApproved && linkedInLeadSyncEnabled(environment)
    ? [...withConversions, ...LEAD_SYNC_SCOPES]
    : withConversions;
}
